(() => {
  'use strict';

  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const qs = (sel, root = document) => root.querySelector(sel);
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = () => window.matchMedia('(pointer: coarse)').matches;

  function splitWords(el, mode = 'section') {
    if (!el || el.dataset.mkMotionSplit === '1') return;
    const label = el.textContent.trim();
    if (!label) return;
    el.dataset.mkMotionSplit = '1';
    el.setAttribute('aria-label', label);
    const words = label.split(/\s+/);
    el.innerHTML = words.map((word, i) => (
      `<span class="mk8-word-wrap" aria-hidden="true"><span class="mk8-word mk8-word--${mode}" style="--mk-word:${i}">${word}${i < words.length - 1 ? '&nbsp;' : ''}</span></span>`
    )).join('');
  }

  function animateHeroSlide(slide) {
    if (!slide || reduceMotion()) return;
    splitWords(qs('h1', slide), 'hero');
    slide.classList.remove('mk8-hero-play');
    // Force a reflow so the stagger replays on every slider change.
    void slide.offsetWidth;
    requestAnimationFrame(() => slide.classList.add('mk8-hero-play'));
  }

  function initHeroSlider(root = document) {
    qsa('[data-mk8-slider]', root).forEach((slider) => {
      if (slider.dataset.mk8Ready === '1') return;
      slider.dataset.mk8Ready = '1';

      const slides = qsa('[data-mk8-slide]', slider);
      const dots = qsa('[data-mk8-slide-to]', slider);
      const prev = slider.querySelector('[data-mk8-prev]');
      const next = slider.querySelector('[data-mk8-next]');
      if (!slides.length) return;

      slides.forEach(slide => splitWords(qs('h1', slide), 'hero'));
      let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
      let timer = null;
      let locked = false;

      const show = (nextIndex, userInitiated = false) => {
        if (locked && userInitiated) return;
        if (userInitiated) {
          locked = true;
          window.setTimeout(() => { locked = false; }, 620);
        }
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (!active) slide.classList.remove('mk8-hero-play');
        });
        dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
        animateHeroSlide(slides[index]);
        if (userInitiated) restart();
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (!reduceMotion() && !document.hidden && slides.length > 1) {
          timer = window.setInterval(() => show(index + 1), 6800);
        }
      };
      const restart = () => {
        stop();
        window.setTimeout(start, 260);
      };

      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i, true)));
      prev?.addEventListener('click', () => show(index - 1, true));
      next?.addEventListener('click', () => show(index + 1, true));
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

      let touchX = 0;
      slider.addEventListener('touchstart', (event) => {
        touchX = event.touches[0]?.clientX || 0;
      }, { passive: true });
      slider.addEventListener('touchend', (event) => {
        const endX = event.changedTouches[0]?.clientX || touchX;
        const delta = endX - touchX;
        if (Math.abs(delta) > 48) show(index + (delta < 0 ? 1 : -1), true);
      }, { passive: true });

      show(index);
      start();
    });
  }

  function initSectionMotion(root = document) {
    const groups = [
      ['.mk8-section-head', 'heading'],
      ['.mk8-seasonal__head', 'heading'],
      ['.mk8-category-card', 'card'],
      ['.luxe-product-card', 'card'],
      ['.mk8-mini-cat', 'card'],
      ['.mk8-editorial-promo__card', 'left'],
      ['.mk8-editorial-promo__image', 'right'],
      ['.mk8-feature-banner__media', 'left'],
      ['.mk8-feature-banner__copy', 'right'],
      ['.mk8-story__media', 'left'],
      ['.mk8-story__copy', 'right'],
      ['.mk8-feature', 'card'],
      ['.mk8-value', 'card'],
      ['.mk8-journal-card', 'card'],
      ['.mk8-social-card', 'card'],
      ['.mk8-newsletter__card', 'scale']
    ];

    const nodes = [];
    groups.forEach(([selector, type]) => {
      qsa(selector, root).forEach((node, i) => {
        if (node.dataset.mkMotionReady === '1') return;
        node.dataset.mkMotionReady = '1';
        node.classList.add('mk8-motion', `mk8-motion--${type}`);
        node.style.setProperty('--mk-stagger', `${Math.min((i % 6) * 85, 425)}ms`);
        nodes.push(node);
      });
    });

    qsa('.mk8-section-head h2,.mk8-seasonal__head h2,.mk8-story__copy h2,.mk8-feature-banner__copy h2,.mk8-newsletter__copy h2', root)
      .forEach(el => splitWords(el, 'section'));

    if (!nodes.length) return;
    if (reduceMotion() || !('IntersectionObserver' in window)) {
      nodes.forEach(node => node.classList.add('is-mk8-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-mk8-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -7% 0px' });

    nodes.forEach(node => observer.observe(node));
  }

  function initImageParallax(root = document) {
    if (reduceMotion()) return;
    const items = qsa(
      '.mk8-editorial-promo__image img,.mk8-feature-banner__media img,.mk8-story__media>img,.mk8-journal-card__media img,.mk8-social-card img',
      root
    );
    items.forEach(img => img.classList.add('mk8-parallax-image'));
    if (!items.length) return;

    let raf = 0;
    const update = () => {
      const vh = window.innerHeight || 800;
      items.forEach((img) => {
        const host = img.parentElement;
        const rect = host.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > vh + 100) return;
        const center = rect.top + rect.height / 2;
        const progress = (center - vh / 2) / vh;
        const amount = Math.max(-18, Math.min(18, progress * -28));
        img.style.setProperty('--mk-parallax-y', `${amount}px`);
      });
      raf = 0;
    };
    const request = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    update();
  }

  function initPointerDetails(root = document) {
    if (coarsePointer() || reduceMotion()) return;

    qsa('.mk8-category-card,.luxe-product-card,.mk8-journal-card,.mk8-social-card', root).forEach(card => {
      if (card.dataset.mkHoverReady === '1') return;
      card.dataset.mkHoverReady = '1';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        card.style.setProperty('--mk-cursor-x', `${x * 100}%`);
        card.style.setProperty('--mk-cursor-y', `${y * 100}%`);
      });
    });

    qsa('.mk8-btn,.mk8-text-link,.mk8-hero__arrows button', root).forEach(button => {
      if (button.dataset.mkMagnetReady === '1') return;
      button.dataset.mkMagnetReady = '1';
      button.classList.add('mk8-magnetic');
      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        button.style.setProperty('--mk-mag-x', `${x * .07}px`);
        button.style.setProperty('--mk-mag-y', `${y * .12}px`);
      });
      button.addEventListener('pointerleave', () => {
        button.style.setProperty('--mk-mag-x', '0px');
        button.style.setProperty('--mk-mag-y', '0px');
      });
    });
  }

  function initHeaderMotion() {
    const header = qs('.mk8-header');
    if (!header || header.dataset.mkMotionHeader === '1') return;
    header.dataset.mkMotionHeader = '1';
    let raf = 0;
    const update = () => {
      header.style.setProperty('--mk-header-progress', String(Math.min(window.scrollY / 160, 1)));
      raf = 0;
    };
    window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    update();
  }

  function initDrawerStagger(root = document) {
    qsa('.mk8-drawer__nav>a', root).forEach((item, i) => item.style.setProperty('--mk-menu-index', String(i)));
  }

  function initTheme(root = document) {
    initHeroSlider(root);
    initSectionMotion(root);
    initImageParallax(root);
    initPointerDetails(root);
    initDrawerStagger(root);
    initHeaderMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTheme());
  } else {
    initTheme();
  }

  document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
})();
